import { IconPencil } from '@tabler/icons-react';
import { BenefitDto, BenefitStatus } from '@zorro/clients';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Drawer, useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { YesNo } from '@zorro/types';
import { Icon, Stack, brand } from '@zorro/web/design-system';
import {
  getEmployeeActions,
  useFlowId,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { isCoverageStartingTodayOrLater } from '../../EnrollmentConfirmationForm/EnrollmentConfirmationUtils';
import { FormFooter } from '../../FormFooter';
import { AgentOrEmployee } from './AgentOrEmployeeInput';
import { PaymentCompletionEmailCard } from './PaymentCompletionEmailCard';
import { PaymentStatusFormFields } from './PaymentStatusFormFields';
import { InitialPremiumProgress } from './PaymentStatusUtils';
import { usePaymentStatusForm } from './usePaymentStatusForm';

type ManagePaymentStatusProps = {
  benefitId: BenefitDto['id'];
  onboardingPeriodId: string;
};

type ManagePaymentStatusContentProps = ManagePaymentStatusProps & {
  onClose: () => void;
};

export const ManagePaymentStatusContent = ({
  benefitId,
  onboardingPeriodId,
  onClose,
}: ManagePaymentStatusContentProps) => {
  const flowId = useFlowId();
  const {
    isLoading: isLoadingPaymentStatus,
    isValid,
    benefit,
    paymentStatusForm,
    onSubmit,
    handleSubmit,
  } = usePaymentStatusForm(benefitId);
  const { tryMutate: sendConfirmationEmail } = useMonolithMutation({
    method: 'enrollmentCommunicationsControllerSendEnrollmentConfirmationEmail',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
  });
  const { data: periodBenefits, isLoading: isLoadingPeriodBenefits } =
    useMonolithQuery({
      method: 'benefitsControllerGetBenefits',
      params: [onboardingPeriodId],
    });
  const [emailChoice, setEmailChoice] = useState<boolean>();
  const [isHeldEmailUnsent, setIsHeldEmailUnsent] = useState(false);
  const { isSubmitting } = paymentStatusForm.formState;
  const isLoading = isLoadingPaymentStatus || isLoadingPeriodBenefits;
  const shouldSendEmail =
    emailChoice ??
    (!!periodBenefits &&
      isCoverageStartingTodayOrLater(periodBenefits.effectiveFrom));

  const [
    initialPremiumHandler,
    initialPremiumCompleted,
    autoPaySetter,
    autoPayCompleted,
  ] = useWatch({
    control: paymentStatusForm.control,
    name: [
      'initialPremiumHandler',
      'initialPremiumCompleted',
      'autoPaySetter',
      'autoPayCompleted',
    ],
  });

  // Completing the payment releases the email held back at confirmation. A
  // failed send is tracked locally: the refetched benefit no longer awaits payment.
  const isCompletingAwaitingPayment =
    (benefit?.status === BenefitStatus.AWAITING_PAYMENT || isHeldEmailUnsent) &&
    initialPremiumCompleted === InitialPremiumProgress.COMPLETED;
  const paymentConditions = {
    isInitialPayments:
      initialPremiumHandler === AgentOrEmployee.EMPLOYEE &&
      initialPremiumCompleted !== InitialPremiumProgress.COMPLETED,
    isAutoPay:
      autoPaySetter === AgentOrEmployee.EMPLOYEE &&
      autoPayCompleted !== YesNo.YES,
  };
  const requiredActions = getEmployeeActions(paymentConditions, true);

  const handleFormSubmit = handleSubmit(async (data) => {
    if (!(await onSubmit(data))) {
      return;
    }
    if (isCompletingAwaitingPayment && shouldSendEmail) {
      const emailResult = await sendConfirmationEmail([onboardingPeriodId], {
        flowId,
      });
      if (!emailResult.isOk()) {
        setIsHeldEmailUnsent(true);
        return;
      }
    }
    onClose();
  });

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{
          onClick: handleFormSubmit,
          disabled: isLoading || !isValid || isSubmitting,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{ onClick: onClose }}
      />
    ),
    [handleFormSubmit, isLoading, isValid, isSubmitting, onClose]
  );

  useDrawerFooter(drawerFooter);

  if (isLoading) {
    return null;
  }

  return (
    <Stack gap="xxl">
      <PaymentStatusFormFields
        paymentStatusForm={paymentStatusForm}
        onboardingPeriodId={onboardingPeriodId}
        shouldShowCompleted
      />
      {isCompletingAwaitingPayment && (
        <PaymentCompletionEmailCard
          isChecked={shouldSendEmail}
          onChange={setEmailChoice}
          requiredActions={requiredActions}
        />
      )}
    </Stack>
  );
};

const ManagePaymentStatus = ({
  benefitId,
  onboardingPeriodId,
}: ManagePaymentStatusProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <ActionIcon
        onClick={() => setIsModalOpen(true)}
        pos="absolute"
        top="5px"
        right="10px"
        size="md"
      >
        <Icon icon={IconPencil} color={brand.zorroGray800} />
      </ActionIcon>

      <Drawer
        onClose={() => setIsModalOpen(false)}
        title="Update payment status"
        position="right"
        opened={isModalOpen}
      >
        {isModalOpen && (
          <ManagePaymentStatusContent
            benefitId={benefitId}
            onboardingPeriodId={onboardingPeriodId}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </Drawer>
    </>
  );
};

export { ManagePaymentStatus };
